import {BI_ORIGINS, ExperimentNames} from '@wix/wix-events-commons-statics'
import {focusElement, hookToAttributeSelector, scrollElementIntoView} from '@wix/panda-js-utils'
import classNames from 'classnames'
import React, {useEffect} from 'react'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useExperiments} from '@wix/yoshi-flow-editor'
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi'
import c from '../classnames.scss'
import alignmentClasses from '../event-details/alignment/alignment.scss'
import {FullDate} from '../event-details/full-date'
import {FullLocation} from '../event-details/full-location'
import {Map} from '../event-details/map'
import {RecurringDatesButton} from '../event-details/recurring-dates-button'
import {RecurringDatesCount} from '../event-details/recurring-dates-count'
import {SecondaryRsvpButton} from '../event-details/secondary-rsvp-button'
import {Members} from '../members'
import {SocialShareWithTitle} from '../social-share/with-title'
import {Subtitle} from '../subtitle'
import {RegistrationButton} from '../event-details/registration-button'
import {GroupActivity} from '../event-details/group-activity'
import {MobileTicketsPicker as MobileTicketsPickerOld} from '../checkout/mobile-checkout/mobile-tickets-picker'
import {useSettings} from '../../../hooks/settings'
import {settingsParams} from '../../../settingsParams'
import {ShortDateLocation} from '../event-details/header/short-date-location'
import {FormEntry, SeatingPlanModal} from '../chunks'
import {AboutSection} from '../event-details/about-section'
import {useVisibilityStyles} from '../../../hooks/use-visibility-styles'
import {MembershipOffers} from '../event-details/membership-offers'
import {usePageStateSelector} from '../../hooks/state'
import {isPaidPlansVisible} from '../../selectors/paid-plans'
import {MobileTicketsPicker} from '../event-details/tickets-picker-new/lazy-tickets-picker'
import {SLOT_EVENT_DETAILS_CONTENT, SLOT_EVENT_DETAILS_HEADER} from '../../../../../commons/constants/slots'
import {MobileSchedule} from './mobile-schedule'
import s from './event-details-mobile.scss'
import {MobileImage} from './mobile-image'
import {Description} from './description'
import {EventDetailsProps, ContainerProps} from '.'

export const EventDetailsMobile = ({
  componentSettings,
  eventTitle,
  membersVisible,
  t,
  mapVisible,
  aboutSectionVisible,
  scheduleVisible,
  sideBySideHeaderLayout,
  detailsPageLoaded,
  handleRSVP,
  mainImage,
  eventDescription,
  moreDatesVisible,
  moreDatesButtonVisible,
  groupVisible,
  hasTickets,
  ticketedEvent,
  withSeatingPlan,
  openSeatingPlan,
}: EventDetailsProps) => {
  const {experiments} = useExperiments()
  const paidPlansVisible = usePageStateSelector(isPaidPlansVisible)
  const slotsEnabled = experiments.enabled(ExperimentNames.EventDetailsSlots)

  const {
    allBreakpoints: {isMembersVisible, isGroupVisible, isScheduleVisible, isMembershipsVisible},
  } = useVisibilityStyles()

  useEffect(() => {
    detailsPageLoaded()
    focusElement({selector: hookToAttributeSelector(DH.eventDetails), preventScroll: true})
  }, [])

  const ticketPickerRedesignEnabled = experiments.enabled(ExperimentNames.TicketPickerRedesign)

  const isImageVisible = () => Boolean(mainImage)

  const handleRegistrationButtonClick = () => {
    if (ticketedEvent) {
      if (withSeatingPlan) {
        SeatingPlanModal.preload()
        return openSeatingPlan()
      } else {
        return scrollToTicketsPicker()
      }
    } else {
      FormEntry.preload()
      return handleRSVP()
    }
  }

  const settings = useSettings(componentSettings)
  const timeAndLocationTitleText = settings.get(settingsParams.timeAndLocationTitleText)
  const guestsTitleText = settings.get(settingsParams.guestsTitleText)
  const scheduleTitleText = settings.get(settingsParams.scheduleTitleText)

  return (
    <section data-hook={DH.eventDetails} className={s.view} aria-label={eventTitle}>
      <div className={classNames(s.background, c.evBackgroundColor)} />
      <div className={classNames({[s.borderWrapper]: sideBySideHeaderLayout})}>
        {isImageVisible() ? <MobileImage /> : null}
        <div className={s.header}>
          <div className={s.headerBackground} />
          <div className={s.content}>
            <div className={alignmentClasses.headerAlignment}>
              <ShortDateLocation mobile />
              <h1 className={classNames(s.title, c.evTitleFont, c.evTitleColor)} data-hook={DH.headerEventTitle}>
                {eventTitle}
              </h1>
              {slotsEnabled ? (
                <div className={s.slotHeaderContainer}>
                  <SlotsPlaceholder slotId={SLOT_EVENT_DETAILS_HEADER} />
                </div>
              ) : null}
              <Description description={eventDescription} />
            </div>
          </div>
          <RegistrationButton
            className={classNames(s.registrationButton, alignmentClasses.headerAlignment)}
            onClick={handleRegistrationButtonClick}
            disableSpacing
            narrowerButton
          />
        </div>
      </div>
      <Container visible dataHook="dateLocationContainer">
        <Subtitle
          className={classNames(s.eventInfoTitle, ticketPickerRedesignEnabled ? undefined : s.subtitleSize)}
          dataHook={DH.subtitleTimeAndLocation}
          text={timeAndLocationTitleText}
        />
        <div className={!moreDatesButtonVisible ? s.dateLocationWithoutDatesButton : null}>
          <div className={classNames(s.timeAndLocation, c.evTextFont, c.evTextColor)}>
            <FullDate />
            <FullLocation />
          </div>
          {moreDatesVisible && <RecurringDatesCount className={s.recurrentDatesCount} disableSpacing />}
        </div>
      </Container>
      <Container visible={moreDatesButtonVisible} dataHook="recurringDatesButtonContainer">
        <RecurringDatesButton className={s.recurrentDatesButton} narrowerButton />
      </Container>
      {isMembersVisible() ? (
        <Container className={s.membersContainer} visible={membersVisible} dataHook="membersContainer">
          <Subtitle
            className={classNames(s.eventInfoTitle, ticketPickerRedesignEnabled ? undefined : s.subtitleSize)}
            dataHook={DH.subtitleMembers}
            text={guestsTitleText}
          />
          <div className={s.members}>
            <Members />
          </div>
        </Container>
      ) : null}
      <Container className={s.aboutSectionContainer} visible={aboutSectionVisible} dataHook="aboutSectionContainer">
        <AboutSection />
      </Container>
      {isGroupVisible() ? (
        <Container className={s.groupContainer} visible={groupVisible} dataHook="groupsContainer">
          <GroupActivity className={s.groupActivity} />
        </Container>
      ) : null}
      {isScheduleVisible() ? (
        <Container className={s.scheduleContainer} visible={scheduleVisible} dataHook="scheduleContainer">
          <Subtitle
            className={classNames(s.eventInfoTitle, ticketPickerRedesignEnabled ? undefined : s.subtitleSize)}
            dataHook={DH.SCHEDULE_SUBTITLE}
            text={scheduleTitleText}
          />
          <MobileSchedule />
        </Container>
      ) : null}
      {slotsEnabled ? (
        <div className={s.slotDetailsContainer}>
          <SlotsPlaceholder slotId={SLOT_EVENT_DETAILS_CONTENT} />
        </div>
      ) : null}
      <Container
        visible={hasTickets}
        dataHook="ticketPickerContainer"
        className={ticketPickerRedesignEnabled ? s.ticketPickerContainer : undefined}
      >
        {ticketPickerRedesignEnabled ? (
          <>
            {paidPlansVisible && isMembershipsVisible() ? (
              <div className={s.membershipOffers}>
                <MembershipOffers upgrade={ticketPickerRedesignEnabled} />
              </div>
            ) : null}
            <MobileTicketsPicker />
          </>
        ) : (
          <MobileTicketsPickerOld />
        )}
      </Container>
      <SecondaryRsvpButton onClick={handleRegistrationButtonClick} narrowerButton />
      {mapVisible && (
        <div className={s.mapSpacing}>
          <Map />
        </div>
      )}
      <Container className={s.socialShare} visible={true} dataHook="socialShareContainer">
        <SocialShareWithTitle page={BI_ORIGINS.EVENT_DETAILS} t={t} />
      </Container>
    </section>
  )
}

const Container = ({visible, children, dataHook, className}: ContainerProps) =>
  visible ? (
    <div className={classNames(s.content, alignmentClasses.contentAlignment, className)} data-hook={dataHook}>
      {children}
    </div>
  ) : (
    <div />
  )

const scrollToTicketsPicker = () => {
  scrollElementIntoView(hookToAttributeSelector(DH.TICKETS_PICKER), {block: 'start', behavior: 'smooth'})
}
